import { useEffect, useMemo, useState } from 'react';
import { HomeNavButton } from './NavButtons';
import JalaliDateRangeFilter from './JalaliDateRangeFilter';
import { getStudentClasses } from '../services/classService';
import {
  flattenClassSubjects,
  formatClassSubjectOptionLabel,
  getClassTitle,
  getSubjectTitle,
  getTeacherFromItem,
  getTeacherName,
} from '../utils/classSubjectUtils';
import { formatDateRangeLabel, formatJalaliDate, getDefaultDateRange } from '../utils/jalaliDate';

const rangeOptions = [
  { id: 'all', label: 'همه' },
  { id: '7', label: '۷ روز اخیر' },
  { id: '30', label: '۳۰ روز اخیر' },
];

function formatApiDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function getDateRange(rangeId) {
  if (rangeId === 'all') {
    return { startDate: '', endDate: '' };
  }

  const endDate = new Date();
  const startDate = new Date();
  startDate.setDate(endDate.getDate() - Number(rangeId) + 1);

  return {
    startDate: formatApiDate(startDate),
    endDate: formatApiDate(endDate),
  };
}

function getRangeLabel(rangeId) {
  const range = getDateRange(rangeId);

  if (!range.startDate || !range.endDate) {
    return 'همه تاریخ‌ها';
  }

  return `از ${formatJalaliDate(range.startDate)} تا ${formatJalaliDate(range.endDate)}`;
}

function MessageCard({ message, tone = 'neutral' }) {
  return (
    <div className="rounded-[8px] bg-[#F8F8F8] px-4 py-5 text-center">
      <p className={`text-[12px] ${tone === 'error' ? 'font-medium text-[#E02727]' : 'text-slate-500'}`}>
        {message}
      </p>
    </div>
  );
}

export default function DatedRecordsReport({
  title,
  subtitle,
  fetchReport,
  exportReport,
  countsKey,
  countValueKey,
  countLabelKey,
  valueKey,
  labelKey,
  accentClassName = 'bg-[#00C0B9] text-white',
  idleChipClassName = 'border border-slate-200 bg-white text-slate-600',
  showHomeButton = true,
  embedded = false,
  showTeacher = true,
  useJalaliDateRange = false,
}) {
  const defaultRange = useMemo(() => getDefaultDateRange(30), []);
  const [classes, setClasses] = useState([]);
  const [classSubjects, setClassSubjects] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedClassSubjectId, setSelectedClassSubjectId] = useState('');
  const [selectedRange, setSelectedRange] = useState('30');
  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [report, setReport] = useState({
    startDate: null,
    endDate: null,
    totalRecords: 0,
    records: [],
    [countsKey]: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const filteredSubjects = useMemo(() => {
    if (!selectedClassId) {
      return classSubjects;
    }

    return classSubjects.filter((item) => item.classId === selectedClassId || item.class?.id === selectedClassId);
  }, [classSubjects, selectedClassId]);

  const activeDateRange = useJalaliDateRange
    ? { startDate, endDate }
    : getDateRange(selectedRange);

  useEffect(() => {
    let isMounted = true;

    async function loadFilters() {
      try {
        const classItems = await getStudentClasses();

        if (!isMounted) {
          return;
        }

        setClasses(classItems || []);
        setClassSubjects(flattenClassSubjects(classItems));
      } catch {
        if (isMounted) {
          setClasses([]);
          setClassSubjects([]);
        }
      }
    }

    loadFilters();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (
      selectedClassSubjectId &&
      !filteredSubjects.some((item) => item.id === selectedClassSubjectId)
    ) {
      setSelectedClassSubjectId('');
    }
  }, [filteredSubjects, selectedClassSubjectId]);

  useEffect(() => {
    let isMounted = true;

    async function loadReport() {
      setIsLoading(true);
      setErrorMessage('');

      try {
        const data = await fetchReport({
          classId: selectedClassId || undefined,
          classSubjectId: selectedClassSubjectId || undefined,
          startDate: activeDateRange.startDate || undefined,
          endDate: activeDateRange.endDate || undefined,
        });

        if (isMounted) {
          setReport(data);
        }
      } catch (error) {
        if (isMounted) {
          setErrorMessage(error.message || 'امکان دریافت گزارش وجود ندارد.');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    loadReport();

    return () => {
      isMounted = false;
    };
  }, [
    fetchReport,
    selectedClassId,
    selectedClassSubjectId,
    activeDateRange.startDate,
    activeDateRange.endDate,
  ]);

  const handleExport = async () => {
    if (!exportReport) {
      return;
    }

    setIsExporting(true);
    setErrorMessage('');

    try {
      await exportReport({
        classId: selectedClassId || undefined,
        classSubjectId: selectedClassSubjectId || undefined,
        startDate: activeDateRange.startDate || undefined,
        endDate: activeDateRange.endDate || undefined,
      });
    } catch (error) {
      setErrorMessage(error.message || 'دریافت فایل خروجی ناموفق بود.');
    } finally {
      setIsExporting(false);
    }
  };

  const counts = report?.[countsKey] || [];
  const records = report?.records || [];
  const rangeLabel = useJalaliDateRange
    ? formatDateRangeLabel(startDate, endDate)
    : getRangeLabel(selectedRange);

  return (
    <div className="space-y-5">
      {!embedded && (
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-[18px] font-semibold text-slate-800">{title}</h1>
            <p className="mt-1 text-[12px] leading-5 text-slate-500">{subtitle}</p>
          </div>
          {showHomeButton ? <HomeNavButton /> : null}
        </div>
      )}

      {classes.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={() => setSelectedClassId('')}
            className={`shrink-0 rounded-[8px] px-3 py-1.5 text-[11px] transition-colors ${
              !selectedClassId ? accentClassName : idleChipClassName
            }`}
          >
            همه کلاس‌ها
          </button>
          {classes.map((classItem) => {
            const isActive = selectedClassId === classItem.id;

            return (
              <button
                key={classItem.id}
                type="button"
                onClick={() => setSelectedClassId(classItem.id)}
                className={`shrink-0 rounded-[8px] px-3 py-1.5 text-[11px] transition-colors ${
                  isActive ? accentClassName : idleChipClassName
                }`}
              >
                {classItem.title}
              </button>
            );
          })}
        </div>
      )}

      {filteredSubjects.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={() => setSelectedClassSubjectId('')}
            className={`shrink-0 rounded-[8px] px-3 py-1.5 text-[11px] transition-colors ${
              !selectedClassSubjectId ? accentClassName : idleChipClassName
            }`}
          >
            همه درس‌ها
          </button>
          {filteredSubjects.map((classSubject) => {
            const isActive = selectedClassSubjectId === classSubject.id;

            return (
              <button
                key={classSubject.id}
                type="button"
                onClick={() => setSelectedClassSubjectId(classSubject.id)}
                className={`shrink-0 rounded-[8px] px-3 py-1.5 text-[11px] transition-colors ${
                  isActive ? accentClassName : idleChipClassName
                }`}
              >
                {formatClassSubjectOptionLabel(classSubject)}
              </button>
            );
          })}
        </div>
      )}

      <div className="rounded-[12px] border border-slate-100 bg-white p-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-[14px] font-medium text-slate-800">{title}</p>
            <p className="mt-1 text-[12px] text-slate-500">{rangeLabel}</p>
          </div>
          <div className="flex items-center gap-2">
            {exportReport ? (
              <button
                type="button"
                onClick={handleExport}
                disabled={isExporting}
                className="rounded-[8px] border border-slate-200 bg-white px-3 py-1.5 text-[11px] text-slate-700 disabled:opacity-60"
              >
                {isExporting ? 'در حال دریافت...' : 'خروجی اکسل'}
              </button>
            ) : null}
            <span className="rounded-full bg-[#F8F8F8] px-3 py-1 text-[11px] text-slate-500">
              {report.totalRecords || records.length || 0} رکورد
            </span>
          </div>
        </div>

        {useJalaliDateRange ? (
          <JalaliDateRangeFilter
            startDate={startDate}
            endDate={endDate}
            onChange={({ startDate: nextStartDate, endDate: nextEndDate }) => {
              setStartDate(nextStartDate);
              setEndDate(nextEndDate);
            }}
          />
        ) : (
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
            {rangeOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setSelectedRange(option.id)}
                className={`shrink-0 rounded-[8px] px-3 py-1.5 text-[11px] ${
                  selectedRange === option.id ? accentClassName : 'bg-[#F8F8F8] text-slate-600'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}

        {errorMessage ? (
          <div className="mt-4">
            <MessageCard message={errorMessage} tone="error" />
          </div>
        ) : isLoading ? (
          <div className="mt-4">
            <MessageCard message="در حال دریافت گزارش..." />
          </div>
        ) : (
          <>
            {counts.length > 0 && (
              <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
                {counts.map((item) => (
                  <div
                    key={item[countValueKey] || item[countLabelKey]}
                    className="rounded-[8px] bg-[#F8F8F8] px-2 py-2 text-center"
                  >
                    <p className="text-[10px] font-light text-slate-500">
                      {item[countLabelKey] || item[countValueKey]}
                    </p>
                    <p className="mt-1 text-[17px] font-semibold text-slate-800">{item.count}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 flex flex-col gap-2">
              {records.length === 0 ? (
                <MessageCard message="برای این بازه رکوردی ثبت نشده است." />
              ) : (
                records.map((record) => {
                  const description = record.description;
                  const showDetails = showTeacher || Boolean(description);

                  return (
                    <div key={record.id} className="rounded-[10px] border border-slate-100 bg-[#FAFAFA] p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <p className="text-[13px] font-medium text-slate-800">
                            {record[labelKey] || record[valueKey] || 'نامشخص'}
                          </p>
                          <p className="text-[11px] text-slate-500">{formatJalaliDate(record.date)}</p>
                        </div>

                        <span className="rounded-[6px] bg-white px-2 py-1 text-[10px] text-slate-500">
                          {[getSubjectTitle(record), getClassTitle(record, '')].filter(Boolean).join(' · ') || 'کلاس'}
                        </span>
                      </div>

                      {showDetails ? (
                        <div className="mt-2 grid grid-cols-1 gap-2 text-[11px] text-slate-600">
                          {showTeacher ? (
                            <p>معلم: {getTeacherName(getTeacherFromItem(record))}</p>
                          ) : null}
                          {description ? (
                            <p className="rounded-[6px] bg-white px-2 py-2 leading-5">
                              توضیحات: {description}
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
